import { useEffect, useRef } from 'react';
import { BUG_VARIANTS, drawPin, onBugSpriteReady } from '@vd/annotation-core';

/** The box `drawPin` draws a marker into: the disc and its tail, with room for the shadow. */
const BOX_W = 36;
const BOX_H = 40;
/** Where the tail's tip sits in that box. */
const TIP_Y = 34;

/**
 * A ticket type's marker as it appears on a page (TOADO-526).
 *
 * Drawn by `drawPin`, the renderer the extension, the widget's team frame and the
 * ticket's annotation canvas all use, so the settings pages show the marker people
 * will see rather than a separate set of icons that could drift from it. `bug` is
 * the bug style's sprite, shown as its first species (a pin on a page has one of
 * five), which decodes once per document: drawn with the hand-drawn fallback
 * until then, and again when it is ready.
 */
export function TypeMarker({ glyph, height = 40 }: { glyph: string; height?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const width = (BOX_W * height) / BOX_H;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const paint = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const scale = (height / BOX_H) * dpr;
      canvas.width = Math.round(BOX_W * scale);
      canvas.height = Math.round(BOX_H * scale);
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.clearRect(0, 0, BOX_W, BOX_H);
      drawPin(ctx, glyph === 'bug'
        // Without a species the sprite has no tile, and the fallback would stay.
        ? { x: BOX_W / 2, y: TIP_Y, style: 'bug', bugVariant: BUG_VARIANTS[0] }
        : { x: BOX_W / 2, y: TIP_Y, style: 'pin', typeGlyph: glyph });
    };
    paint();
    return glyph === 'bug' ? onBugSpriteReady(paint) : undefined;
  }, [glyph, height]);

  return <canvas ref={ref} style={{ width, height, display: 'block' }} aria-hidden="true" data-glyph={glyph} />;
}
